import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Wallet,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  Calendar,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateEarnings() {
  const { getAuthHeaders } = useAuth();
  const [earnings, setEarnings] = useState({ ledger: [], summary: {} });
  const [payouts, setPayouts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    period_start: "",
    period_end: "",
    amount: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { ...getAuthHeaders(), "Content-Type": "application/json" };
      
      const [earningsRes, payoutsRes, profileRes] = await Promise.all([
        fetch(`${API_URL}/api/affiliate/earnings`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/affiliate/payouts`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/affiliate/profile`, { credentials: "include", headers }),
      ]);

      if (earningsRes.ok) setEarnings(await earningsRes.json());
      if (payoutsRes.ok) setPayouts(await payoutsRes.json());
      if (profileRes.ok) setProfile(await profileRes.json());
    } catch (error) {
      toast.error("Failed to load earnings data");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/affiliate/payouts/request`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payoutForm),
      });

      if (response.ok) {
        toast.success("Payout request submitted!");
        setPayoutDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to request payout");
      }
    } catch (error) {
      toast.error("Failed to request payout");
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/affiliate/export/my-earnings.csv`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "my-earnings.csv";
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded!");
      }
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPayableAmount = () => {
    return earnings.summary?.payable?.total || 0;
  };

  const getPayoutStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-amber-400" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Earnings" subtitle="Track your earnings and request payouts">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Earnings" subtitle="Track your earnings and request payouts">
      <div data-testid="affiliate-earnings" className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">
              {formatCurrency(earnings.summary?.pending?.total)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {earnings.summary?.pending?.count || 0} transactions
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Wallet className="w-4 h-4" />
              <span className="text-sm">Payable</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              {formatCurrency(earnings.summary?.payable?.total)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {earnings.summary?.payable?.count || 0} transactions
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Paid Out</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">
              {formatCurrency(earnings.summary?.paid?.total)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {earnings.summary?.paid?.count || 0} transactions
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Total Earned</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(
                (earnings.summary?.pending?.total || 0) +
                (earnings.summary?.payable?.total || 0) +
                (earnings.summary?.paid?.total || 0)
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            onClick={() => {
              const today = new Date();
              const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
              setPayoutForm({
                period_start: thirtyDaysAgo.toISOString().split("T")[0],
                period_end: today.toISOString().split("T")[0],
                amount: getPayableAmount(),
              });
              setPayoutDialogOpen(true);
            }}
            disabled={getPayableAmount() === 0}
            data-testid="request-payout-btn"
            className="glow-primary"
          >
            <Wallet className="w-4 h-4 mr-2" /> Request Payout
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-earnings-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Payout History */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Payout History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length > 0 ? (
                  payouts.map((payout) => (
                    <tr key={payout.id}>
                      <td className="text-muted-foreground">{formatDate(payout.requested_at)}</td>
                      <td className="text-muted-foreground">
                        {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                      </td>
                      <td className="text-foreground font-medium">{formatCurrency(payout.amount)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {getPayoutStatusIcon(payout.status)}
                          <span
                            className={`capitalize ${
                              payout.status === "paid"
                                ? "text-emerald-400"
                                : payout.status === "rejected"
                                ? "text-red-400"
                                : "text-amber-400"
                            }`}
                          >
                            {payout.status}
                          </span>
                        </div>
                      </td>
                      <td className="text-muted-foreground font-mono text-sm">
                        {payout.payment_reference || "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-slate-500 py-8">
                      No payout requests yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Earnings Ledger */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Recent Earnings</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Merchant</th>
                  <th>Fee</th>
                  <th>Your Earnings</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {earnings.ledger.length > 0 ? (
                  earnings.ledger.slice(0, 20).map((entry) => (
                    <tr key={entry.id}>
                      <td className="text-muted-foreground">{formatDate(entry.created_at)}</td>
                      <td className="text-foreground">{entry.merchant_name || "-"}</td>
                      <td className="text-muted-foreground">{formatCurrency(entry.fee_amount)}</td>
                      <td className="text-emerald-400 font-medium">
                        {formatCurrency(entry.affiliate_share_amount)}
                      </td>
                      <td>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            entry.status === "paid"
                              ? "status-paid"
                              : entry.status === "payable"
                              ? "status-active"
                              : "status-pending"
                          }`}
                        >
                          {entry.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-slate-500 py-8">
                      No earnings yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payout Request Dialog */}
        <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Request Payout</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRequestPayout} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Period Start</Label>
                  <Input
                    type="date"
                    value={payoutForm.period_start}
                    onChange={(e) => setPayoutForm({ ...payoutForm, period_start: e.target.value })}
                    required
                    className="bg-background border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Period End</Label>
                  <Input
                    type="date"
                    value={payoutForm.period_end}
                    onChange={(e) => setPayoutForm({ ...payoutForm, period_end: e.target.value })}
                    required
                    className="bg-background border-border text-foreground"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Amount (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payoutForm.amount}
                  onChange={(e) => setPayoutForm({ ...payoutForm, amount: parseFloat(e.target.value) })}
                  required
                  className="bg-background border-border text-foreground"
                />
                <p className="text-xs text-slate-500">
                  Available: {formatCurrency(getPayableAmount())}
                </p>
              </div>
              
              {profile && (
                <div className="p-4 rounded-lg bg-background border border-border">
                  <p className="text-sm text-muted-foreground mb-2">Payout Method</p>
                  <p className="text-foreground font-medium">
                    {profile.payout_method || "Not set"} 
                    {profile.wallet_address && ` - ${profile.wallet_address.slice(0, 10)}...`}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPayoutDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-payout-btn" className="flex-1 glow-primary">
                  Submit Request
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
